import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

const TOKEN_KEY = "polibest_session_token";
const USER_KEY = "polibest_user";

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [deniedEmail, setDeniedEmail] = useState("");

  const getToken = () => localStorage.getItem(TOKEN_KEY);

  useEffect(() => {
    const interceptor = axios.interceptors.request.use((config) => {
      const token = getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
    return () => axios.interceptors.request.eject(interceptor);
  }, []);

  const checkAuth = async () => {
    const token = getToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      localStorage.setItem(USER_KEY, JSON.stringify(response.data));
      setAccessDenied(false);
    } catch (error) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const savedUser = localStorage.getItem(USER_KEY);
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (e) {
        // ignore invalid JSON
      }
    }
    checkAuth();
  }, []);

  const loginWithPassword = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      if (response.status === 200 && response.data.session_token) {
        localStorage.setItem(TOKEN_KEY, response.data.session_token);
        const { session_token, ...userData } = response.data;
        localStorage.setItem(USER_KEY, JSON.stringify(userData));
        setUser(userData);
        setAccessDenied(false);
        return { success: true };
      }
    } catch (error) {
      if (error.response?.status === 403) {
        setAccessDenied(true);
        setDeniedEmail(email);
        return { success: false, reason: "access_denied" };
      }
      if (error.response?.status === 401) {
        return { success: false, reason: "invalid_password" };
      }
      return { success: false, reason: "error" };
    }
    return { success: false, reason: "error" };
  };

  const logout = async () => {
    const token = getToken();
    try {
      if (token) {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      // ignore logout errors
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
    setAccessDenied(false);
    setDeniedEmail("");
  };

  const clearAccessDenied = () => {
    setAccessDenied(false);
    setDeniedEmail("");
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      accessDenied,
      deniedEmail,
      loginWithPassword,
      logout,
      checkAuth,
      clearAccessDenied
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
